<div class="crews-tab-toolbar">
    <span class="crews-range-label">
        Time off: {{ rangeStart() | date: 'mediumDate' }} – {{ rangeEnd() | date: 'mediumDate' }}
    </span>
    <div class="crews-legend">
        <span class="legend-item legend-approved">Approved</span>
        <span class="legend-item legend-pending">Pending</span>
        <span class="legend-item legend-holiday">Company holiday</span>
        <span class="legend-item legend-reassignment">Temporary reassignment</span>
    </div>
    <span class="text-muted crews-session-note">Reassignments are session-only — refresh clears them.</span>
    <button type="button" class="btn btn-outline-secondary btn-sm" (click)="refresh()" [disabled]="isLoading()">
        {{ isLoading() ? 'Loading…' : 'Refresh' }}
    </button>
</div>

@if (reassignments().length) {
    <div class="crews-reassignment-list">
        <div class="crews-reassignment-list-title">Temporary reassignments</div>
        <div class="table-responsive">
            <table class="table table-sm table-striped crews-reassignment-table mb-0">
                <thead>
                    <tr>
                        <th>Member</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Dates</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    @for (item of reassignments(); track item.id) {
                        <tr>
                            <td>{{ item.memberName }}</td>
                            <td>{{ item.fromForemanName }}</td>
                            <td>{{ item.toForemanName }}</td>
                            <td>{{ item.startDate | date: 'mediumDate' }} – {{ item.endDate | date: 'mediumDate' }}</td>
                            <td class="text-end">
                                <button type="button" class="btn btn-outline-danger btn-sm" (click)="removeReassignment(item.id)">
                                    Remove
                                </button>
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    </div>
}

@if (isLoading() && !timeline()) {
    <p class="text-muted crews-status">Loading crews and time off…</p>
} @else if (loadError()) {
    <p class="text-danger crews-status">{{ loadError() }}</p>
} @else if (!foremanGroups().length) {
    <p class="text-muted crews-status">No foremen found.</p>
} @else {
    <div class="crews-timeline-scroll">
        <div class="crews-timeline-grid">
            <div class="crews-timeline-header">
                <div class="crews-name-col crews-header-cell">Foreman / Crew</div>
                <div class="crews-chart-col">
                    <div class="crews-axis">
                        @for (tick of timelineTicks(); track tick.label + tick.leftPct) {
                            <span class="crews-axis-tick" [style.left.%]="tick.leftPct">{{ tick.label }}</span>
                        }
                    </div>
                </div>
            </div>

            @if (holidayMarkers().length) {
                <div class="crews-holiday-row">
                    <div class="crews-name-col crews-holiday-label">Company holidays</div>
                    <div class="crews-chart-col">
                        <div class="crews-track">
                            @for (marker of holidayMarkers(); track marker.date + marker.name) {
                                <span
                                    class="crews-bar crews-bar-holiday"
                                    [class.crews-bar-holiday-partial]="marker.isPartialDay"
                                    [style.left.%]="marker.leftPct"
                                    [style.width.%]="marker.widthPct"
                                    [title]="marker.tooltip"
                                >{{ marker.shortLabel }}</span>
                            }
                        </div>
                    </div>
                </div>
            }

            @for (group of foremanGroups(); track group.hrRef) {
                <div class="crews-foreman-row">
                    <button type="button" class="crews-name-col crews-foreman-toggle" (click)="toggleForeman(group.hrRef)">
                        <span class="crews-toggle-icon">{{ group.expanded ? '▼' : '▶' }}</span>
                        <span class="crews-foreman-name">{{ group.name }}</span>
                        <span class="text-muted crews-role">{{ group.jobTitle }}</span>
                    </button>
                    <div class="crews-chart-col crews-foreman-chart">
                        <div class="crews-track crews-track-muted"></div>
                    </div>
                </div>

                @if (group.expanded) {
                    @for (member of group.members; track member.hrRef) {
                        <div class="crews-member-row" [class.crews-member-foreman]="member.isForeman">
                            <div class="crews-name-col crews-member-name">
                                <span>{{ member.name }}</span>
                                @if (member.isForeman) {
                                    <span class="badge bg-secondary crews-foreman-badge">Foreman</span>
                                }
                                @if (member.jobTitle) {
                                    <span class="text-muted crews-role">{{ member.jobTitle }}</span>
                                }
                                @if (!member.isForeman) {
                                    <button
                                        type="button"
                                        class="btn btn-link btn-sm crews-reassign-btn"
                                        (click)="openReassignmentModal(group, member)"
                                    >
                                        Reassign temporarily…
                                    </button>
                                }
                            </div>
                            <div class="crews-chart-col">
                                <div class="crews-track">
                                    @for (bar of member.bars; track bar.startDate + bar.endDate + bar.status + bar.leaveType) {
                                        <span
                                            class="crews-bar"
                                            [class.crews-bar-approved]="bar.status === 'Approved'"
                                            [class.crews-bar-pending]="bar.status === 'Pending'"
                                            [class.crews-bar-reassignment]="bar.status === 'Reassignment'"
                                            [style.left.%]="bar.leftPct"
                                            [style.width.%]="bar.widthPct"
                                            [title]="bar.tooltip"
                                        >{{ bar.leaveType }}</span>
                                    }
                                </div>
                            </div>
                        </div>
                    }

                    @if (group.borrowedMembers.length) {
                        <div class="crews-borrowed-header-row">
                            <div class="crews-name-col crews-borrowed-label">Borrowed crew</div>
                            <div class="crews-chart-col"></div>
                        </div>
                        @for (borrowed of group.borrowedMembers; track borrowed.reassignmentId) {
                            <div class="crews-member-row crews-member-borrowed">
                                <div class="crews-name-col crews-member-name">
                                    <span>{{ borrowed.name }}</span>
                                    <span class="text-muted crews-role">{{ borrowed.jobTitle }}</span>
                                    <span class="text-muted crews-borrowed-from">from {{ borrowed.fromForemanName }}</span>
                                </div>
                                <div class="crews-chart-col">
                                    <div class="crews-track">
                                        @for (bar of borrowed.bars; track bar.startDate + bar.endDate + bar.leaveType) {
                                            <span
                                                class="crews-bar crews-bar-reassignment"
                                                [style.left.%]="bar.leftPct"
                                                [style.width.%]="bar.widthPct"
                                                [title]="bar.tooltip"
                                            >{{ bar.leaveType }}</span>
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    }
                }
            }
        </div>
    </div>
}
